import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { RenderModule } from 'holocron';
import { Experiment, Variant } from '@americanexpress/one-experiments';
import { inavMarketsDetails } from './InavConfigs';
import InavLite from './InavLite';
import { GLOBAL_HEADER_NAME, INAV_VARIANTS } from '../../utils/constants';

const inavVariants = {
  [INAV_VARIANTS.CONTROL]: RenderModule,
  [INAV_VARIANTS.INAV_VAR_A]: InavLite,
};

const LoadHeader = ({
  type,
  actSetInavData,
  isExperiment,
  showExitModal,
  showExitButtonInHeader = true,
  ...props
}) => {
  const Component = inavVariants[type];
  const [modalData, setModalData] = useState({
    show: showExitModal,
    showExitButtonInHeader,
  });

  // istanbul ignore next
  const handleQuitModal = useCallback(
    (show, modalProps, showLeaveBtnInHeader) => {
      setModalData((prev) => ({
        ...prev,
        modalProps,
        show,
        showExitButtonInHeader: showLeaveBtnInHeader,
      }));
    },
    []
  );

  useEffect(() => {
    if (actSetInavData) {
      actSetInavData({ isExperiment, type });
    }
    window.axpRootModule = {};
    window.axpRootModule.handleQuitModal = handleQuitModal;
  }, [isExperiment]);

  return (
    <Component
      {...(type === INAV_VARIANTS.CONTROL
        ? { moduleName: GLOBAL_HEADER_NAME } : { ...props, modalData, actSetInavData })}
    />
  );
};

LoadHeader.propTypes = {
  type: PropTypes.string,
  isExperiment: PropTypes.bool,
  actSetInavData: PropTypes.func,
  showExitModal: PropTypes.bool,
  showExitButtonInHeader: PropTypes.bool,
};

const RenderInavHeader = ({
  locale,
  childModuleProps = {},
  actSetInavData,
  languageData,
}) => {
  const marketConfig = inavMarketsDetails[locale];
  if (!marketConfig) {
    return <LoadHeader actSetInavData={actSetInavData} type={INAV_VARIANTS.CONTROL} />;
  }

  const {
    inavLiteJourney,
    ignoreInavLiteModules,
    props,
  } = marketConfig;
  const { journey, moduleName: childModuleName } = childModuleProps;

  const showGlobalHeader = !Object.keys(inavLiteJourney)?.includes(journey)
    || ignoreInavLiteModules?.includes(childModuleName);

  const { experimentDetails } = inavLiteJourney?.[journey] || {};

  if (showGlobalHeader || !experimentDetails) {
    return (
      <LoadHeader
        actSetInavData={actSetInavData}
        languageData={languageData}
        type={showGlobalHeader ? INAV_VARIANTS.CONTROL : INAV_VARIANTS.INAV_VAR_A}
        {...props}
      />
    );
  }
  const experimentName = experimentDetails?.experimentName;

  const variantsList = experimentDetails?.variants;

  return (
    <Experiment
      experimentName={experimentName}
      defaultVariant={experimentDetails?.defaultVariant}
      dimension={experimentDetails?.dimensionName}
      LoadingComponent={React.Fragment}
    >
      {variantsList?.map((itm) => (
        <Variant key={itm} variant={itm}>
          <LoadHeader
            languageData={languageData}
            actSetInavData={actSetInavData}
            isExperiment={true}
            type={itm}
            {...props}
          />
        </Variant>
      ))}
    </Experiment>
  );
};

RenderInavHeader.propTypes = {
  locale: PropTypes.string,
  location: PropTypes.objectOf(PropTypes.string),
  childModuleProps: PropTypes.objectOf(PropTypes.string),
  prefetchExperiments: PropTypes.objectOf(PropTypes.string),
  actSetInavData: PropTypes.func,
  languageData: PropTypes.objectOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.object])
  ).isRequired,
};

export default React.memo(RenderInavHeader);
